/**
 * Response Parser Utility
 * Parses and categorizes response data from AI chat API
 */

/**
 * Parse response data and categorize by type
 * @param {Object} data - The data object from the API response
 * @returns {Object} Parsed data with shipment, invoice, and other categories
 */
export const parseResponseData = (data) => {
    if (!data || typeof data !== "object") {
        return { shipment: null, invoice: null, other: {} };
    }

    const result = {
        shipment: null,
        invoice: null,
        other: {},
    };

    // Check invoice data first (more specific)
    for (const [key, value] of Object.entries(data)) {
        if (key === "get_invoice_details" || isInvoiceData(value)) {
            result.invoice = value;
        } else if (key === "get_shipment_status" || isShipmentData(value)) {
            result.shipment = value;
        } else {
            result.other[key] = value;
        }
    }

    return result;
};

/**
 * Check if data represents a shipment
 * @param {*} data - The data to check
 * @returns {boolean} True if data looks like shipment data
 */
const isShipmentData = (data) => {
    if (!data || typeof data !== "object") return false;
    const keys = Object.keys(data);
    return (
        keys.some((k) => k.toLowerCase().includes("shipment")) ||
        (keys.includes("status") && keys.some((k) => k.toLowerCase().includes("origin")))
    );
};

/**
 * Check if data represents an invoice
 * @param {*} data - The data to check
 * @returns {boolean} True if data looks like invoice data
 */
const isInvoiceData = (data) => {
    if (!data || typeof data !== "object") return false;
    const keys = Object.keys(data);
    const keysLower = keys.map((k) => k.toLowerCase());

    // Invoice-specific indicators
    const hasInvoiceField = keysLower.some((k) => k.includes("invoice") && (k.includes("number") || k.includes("status") || k.includes("date")));
    const hasPaymentStatus = keysLower.some((k) => k.includes("payment") && k.includes("status"));
    const hasBillTo = keysLower.some((k) => k === "billtocompany" || k === "bill_to" || k === "billto");
    const hasTotalAmount = keysLower.some((k) => (k === "totalamount" || k === "total_amount" || k === "total") && typeof data[k] === "number");

    return hasInvoiceField || hasPaymentStatus || hasBillTo || hasTotalAmount;
};

/**
 * Format currency value
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code (default: USD)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = "USD") => {
    if (amount == null || isNaN(amount)) return "-";
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency,
        }).format(amount);
    } catch {
        return `${currency} ${amount}`;
    }
};

/**
 * Format date value
 * @param {string|Date} dateString - The date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "-";
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return "-";
    }
};

/**
 * Format date and time value
 * @param {string|Date} dateString - The date to format
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "-";
        return date.toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "-";
    }
};

/**
 * Format a key for display (convert camelCase to Title Case)
 * @param {string} key - The key to format
 * @returns {string} Formatted key
 */
export const formatKey = (key) => {
    return key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
};

/**
 * Format any value for display
 * Enhanced to handle arrays and objects gracefully
 * @param {*} value - The value to format
 * @returns {string} Formatted value
 */
export const formatValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";

    // Handle arrays
    if (Array.isArray(value)) {
        // Empty arrays
        if (value.length === 0) return "-";

        // Arrays of primitives (strings, numbers) - join with comma
        if (value.every(item => typeof item !== "object")) {
            return value.join(", ");
        }

        // Arrays of objects - show count with expandable indicator
        return `${value.length} items`;
    }

    // Handle objects
    if (typeof value === "object") {
        // Show object field count instead of full JSON dump
        const keyCount = Object.keys(value).length;
        return `Object (${keyCount} fields)`;
    }

    return String(value);
};

export default {
    parseResponseData,
    formatCurrency,
    formatDate,
    formatDateTime,
    formatKey,
    formatValue,
    isShipmentData,
    isInvoiceData,
};
